import './MatchIcons.less';

import React, { FC, useContext } from 'react';
import { SvgIcon } from '../../../icons/SvgIcon';
import clsx from 'clsx';
import { loggedIn$ } from '../subs';
import { dispatch, useReframe } from 'nike-re-framejs';
import TurboLiveContext from '../context/TurboLiveContext';
import { MatchDetailBtn } from './Match';
import { setActiveAnalysisTab } from '../handlers';
import { useStreamIcon } from '../hooks/useStreamIcon';
import { openStats } from '../config';
import i18n from '../../../common/i18n';
import { isRadarStream, isVideoStream } from '../push/overview/handlers';
import { useIsBetradarWidgetAvailable } from '../../match-statistics/hooks/useIsBetradarWidgetAvailable';
import { isMobileChannel } from '../../../common/util';
import { activeInfocentreMatchIdTab$ } from '../push/overview/subs';

const ICON_SIZE = '14';
const ICON_SIZE_MOBILE = '16';
const ICON_CLASS = 'fs-0 flex-shrink-0';
const BTN_CLASS = 'match-icon-btn btn-no-pad fs-0';
const TAB_VIDEO = 'video';
const IconPlaceholder = () => <span className="px-8 mx-3 pt-15 pb-3" />;
const MobileIconPlaceholder = () => <span className="px-8 mx-5" />;

export const MatchIcon: FC<{
    imgId;
    title?;
    svgClass?: string;
    onClick?: any;
    'data-atid'?: string;
    disabled?: boolean;
    active?: boolean;
}> = ({ imgId, title, svgClass, onClick, 'data-atid': dataAtid, disabled, active = false }) => {
    const mobileView = isMobileChannel();
    const iconSize = mobileView ? ICON_SIZE_MOBILE : ICON_SIZE;
    const handleClick = e => {
        e.preventDefault();
        e.stopPropagation();
        onClick();
    };
    const svgIcon = (
        <SvgIcon
            imgId={imgId}
            sizeX={iconSize}
            sizeY={iconSize}
            className={clsx(active ? 'c-orange' : 'match-icon', ICON_CLASS, svgClass, 'mx-5', {
                disabled: disabled,
                'my-2': !mobileView
            })}
            dataAtid={isMobileChannel() ? dataAtid : null}
        />
    );
    return isMobileChannel() ? (
        svgIcon
    ) : (
        <button
            onClick={e => (!disabled ? handleClick(e) : null)}
            title={i18n(title)}
            className={clsx(BTN_CLASS, { disabled: disabled, active })}
            data-atid={dataAtid}
        >
            {svgIcon}
        </button>
    );
};

export const VideoIcon: FC<{ match: any }> = ({ match }) => {
    const derefSub = useReframe();
    const active = !isMobileChannel() && derefSub(activeInfocentreMatchIdTab$(match?.get('id'), TAB_VIDEO));
    const { onStreamIconClick } = useStreamIcon(match, TAB_VIDEO);
    const isMatchLive = match?.get('isLive');
    const title = isMatchLive ? 'nui.turbo.live.icon.stream' : 'nui.turbo.live.icon.stream.disabled';
    return (
        <MatchIcon
            imgId="live-stream"
            title={title}
            onClick={isMatchLive ? onStreamIconClick : null}
            data-atid="stream-icon"
            disabled={!isMatchLive}
            active={active}
        />
    );
};

export const RadarIcon: FC<{ match: any; disabled?: boolean }> = ({ match, disabled = false }) => {
    const derefSub = useReframe();
    const { onStreamIconClick } = useStreamIcon(match, 'matchtracker');
    const isBRWidgetAvailable = useIsBetradarWidgetAvailable(match);
    const active = !isMobileChannel() && derefSub(activeInfocentreMatchIdTab$(match?.get('id')));
    if (!isBRWidgetAvailable) {
        return null;
    }
    return (
        <MatchIcon
            imgId="live-radar"
            title="nui.turbo.live.icon.radar"
            onClick={onStreamIconClick}
            data-atid="radar-icon"
            disabled={disabled}
            active={active}
        />
    );
};

export const AnalysisIcon: FC<{ match: any; sportName: string }> = ({ match, sportName }) => {
    const matchId = match?.get('id');
    const onClick = () => {
        dispatch(setActiveAnalysisTab);
    };

    return isMobileChannel() ? (
        <SvgIcon
            imgId={'live-analyse'}
            title={i18n('nui.turbo.live.icon.analysis')}
            sizeX={ICON_SIZE}
            sizeY={ICON_SIZE}
            className={clsx(ICON_CLASS, 'match-icon mx-5')}
        />
    ) : (
        <div className={'match-icon-btn btn-no-pad fs-0'} onClick={onClick}>
            <MatchDetailBtn matchId={matchId} sportName={sportName}>
                <SvgIcon
                    imgId={'live-analyse'}
                    title={i18n('nui.turbo.live.icon.analysis')}
                    sizeX={ICON_SIZE}
                    sizeY={ICON_SIZE}
                    className={clsx(ICON_CLASS, 'match-icon mx-3')}
                />
            </MatchDetailBtn>
        </div>
    );
};

type FavoriteIconProps = {
    btnClass?;
    iconClass?;
    iconSize?: string;
    active: boolean;
    activeTitleKey?: string;
    inactiveTitleKey?: string;
    disabled?: boolean;
    toggle: () => {};
    'data-atid'?: string;
};

export const FavoriteIcon: FC<FavoriteIconProps> = ({
    btnClass,
    iconClass,
    iconSize = '16',
    active,
    activeTitleKey = 'nui.turbo.live.icon.favorite.active',
    inactiveTitleKey = 'nui.turbo.live.icon.favorite',
    disabled = false,
    toggle,
    'data-atid': dataAtid
}) => {
    const { showLoginModal } = useContext(TurboLiveContext);

    const { derefSub } = useReframe();
    const title = active ? i18n(activeTitleKey) : i18n(inactiveTitleKey);
    const onClick = e => {
        e.preventDefault();
        e.stopPropagation();
        const isUserLoggedIn = derefSub(loggedIn$);

        if (isUserLoggedIn) {
            toggle();
        } else {
            showLoginModal();
        }
    };
    return (
        <button
            className={clsx(ICON_CLASS, btnClass, 'match-icon btn-no-pad')}
            title={title}
            onClick={e => onClick(e)}
            disabled={disabled}
            data-atid={dataAtid}
        >
            <SvgIcon
                imgId={`live-favorite${active ? '-active' : ''}`}
                sizeX={iconSize}
                sizeY={iconSize}
                className={clsx('my-10', iconClass)}
            />
        </button>
    );
};

export const StatsIcon: FC<{
    statisticsId;
    iconClass?;
    btnClass?: string;
    isTournament?;
    usePlaceholder?: boolean;
}> = ({ statisticsId, btnClass, iconClass, isTournament, usePlaceholder }) => {
    const isMobileView = isMobileChannel();
    const iconSize = isMobileView ? ICON_SIZE_MOBILE : ICON_SIZE;
    const onClick = e => {
        e.preventDefault();
        e.stopPropagation();
        openStats(statisticsId, isTournament);
    };
    return statisticsId ? (
        <button onClick={onClick} className={clsx(ICON_CLASS, btnClass, 'match-icon')} data-atid="sport-statistics-ico">
            <SvgIcon imgId="live-stats" className={`${iconClass}`} sizeX={iconSize} sizeY={iconSize} />
        </button>
    ) : usePlaceholder ? (
        <span className={clsx('px-15 py-15', { 'mx-3': isMobileView })} />
    ) : null;
};

export const MatchIconsCols = ({ match, sportName }: { match: any; sportName: string }) => {
    const hasVideoStream = isVideoStream(match);
    const hasRadarStream = isRadarStream(match);
    const analysisIds = match?.get('analysisIds');

    return (
        <>
            <div className="flex flex-col justify-center">
                {hasVideoStream ? <VideoIcon match={match} /> : <IconPlaceholder />}
                {hasRadarStream ? <RadarIcon match={match} /> : <IconPlaceholder />}
            </div>
            <div className="flex flex-col justify-center">
                {analysisIds ? <AnalysisIcon match={match} sportName={sportName} /> : <IconPlaceholder />}
                <IconPlaceholder />
            </div>
        </>
    );
};

export const MatchIcons = ({ match, sportName }: { match: any; sportName?: string }) => {
    const hasVideoStream = isVideoStream(match);
    const hasRadarStream = isRadarStream(match);
    const analysisIds = match?.get('analysisIds');

    return (
        <>
            {analysisIds && <AnalysisIcon match={match} sportName={sportName} />}
            {hasVideoStream ? <VideoIcon match={match} /> : <MobileIconPlaceholder />}
            {hasRadarStream ? <RadarIcon match={match} /> : <MobileIconPlaceholder />}
        </>
    );
};
